import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X, LogOut, ShieldCheck, ChevronDown, Bell } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import LanguageSelect from "@/components/LanguageSelect";
import { useAuth } from "@/context/AuthContext";
import { beginSocialPulseLogin, ssoConfigured } from "@/lib/socialpulseOidc";

const LINKS = [
  { name: "Explore", path: "/explore" },
  { name: "Artists", path: "/artists" },
  { name: "Genres", path: "/genres" },
  { name: "Countries", path: "/countries" },
  { name: "Charts", path: "/charts" },
  { name: "Events", path: "/events" },
  { name: "Learn", path: "/instruments" },
  { name: "Community", path: "/community" },
];

const signIn = (returnTo) => {
  if (ssoConfigured()) beginSocialPulseLogin(returnTo);
  else toast.info("SocialPulse SSO is being configured — check back shortly.");
};

const AdminBell = () => {
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    const load = () => api.get("/moderation/pending-count").then((r) => setCount(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <button data-testid="admin-notification-bell" onClick={() => navigate("/moderation")} title="Pending submissions awaiting review" className="relative p-2 rounded-full hover:bg-slate-100 transition-colors">
      <Bell className="w-4.5 h-4.5 text-slate-600" style={{ width: 18, height: 18 }} />
      {count > 0 && (
        <span data-testid="admin-notification-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-pink-600 text-white text-[10px] font-black flex items-center justify-center">
          {count}
        </span>
      )}
    </button>
  );
};

const UserMenu = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="relative">
      <button data-testid="nav-user-menu" onClick={() => setOpen(!open)} className="flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 hover:border-pink-300 transition-colors">
        <span className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 text-white text-xs font-black flex items-center justify-center">
          {user.name?.[0]?.toUpperCase() || "U"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl border border-slate-100 shadow-xl p-2 z-50" data-testid="nav-user-dropdown">
          <p className="px-3 py-2 text-xs text-slate-500 truncate">{user.email}</p>
          {user.role === "admin" && (
            <button data-testid="nav-moderation-link" onClick={() => { setOpen(false); navigate("/moderation"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold text-slate-700 hover:bg-purple-50 hover:text-purple-700">
              <ShieldCheck className="w-4 h-4" /> Moderation Dashboard
            </button>
          )}
          <button data-testid="nav-signout-btn" onClick={() => { setOpen(false); logout(); toast.success("Signed out."); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold text-slate-700 hover:bg-rose-50 hover:text-rose-600">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export const Navbar = () => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setQ("");
      setOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200/80">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <span className="eq-bars" aria-hidden="true"><span /><span /><span /><span /><span /></span>
          <span className="flex flex-col leading-none">
            <span className="font-display font-black text-lg tracking-tight">
              MusicPulse<span className="gradient-text">.pro</span>
            </span>
            <span className="text-[7px] font-black tracking-[0.18em] text-slate-400 uppercase mt-0.5">People · Places · Music · A Brighter World</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center ml-3">
          {LINKS.map((l) => (
            <NavLink
              key={l.name}
              to={l.path}
              data-testid={`nav-link-${l.name.toLowerCase()}`}
              className={({ isActive }) =>
                `px-2.5 py-2 rounded-full text-[13px] font-bold transition-colors ${
                  isActive ? "text-pink-600" : "text-slate-700 hover:text-pink-600"
                }`
              }
            >
              {l.name}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submit} className="hidden xl:flex items-center ml-auto relative">
          <Search className="w-4 h-4 absolute left-3 text-slate-400" />
          <input
            data-testid="nav-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search for artists, places, genres..."
            className="pl-9 pr-4 py-2 w-56 rounded-full border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 focus:bg-white transition-all"
          />
        </form>

        <div className="hidden md:flex items-center gap-2 ml-auto xl:ml-2">
          <LanguageSelect />
          {user?.role === "admin" && <AdminBell />}
          {user ? (
            <UserMenu />
          ) : (
            <button
              data-testid="sso-socialpulse-btn"
              onClick={() => signIn(window.location.pathname)}
              className="text-sm font-bold px-4 py-2 rounded-full border-2 border-slate-200 text-slate-700 hover:border-pink-400 hover:text-pink-600 transition-colors whitespace-nowrap"
            >
              Sign In
            </button>
          )}
          <Link to="/community" data-testid="nav-join-btn" className="gradient-btn text-white text-sm font-bold px-5 py-2 rounded-full">
            Join
          </Link>
        </div>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="lg:hidden ml-auto md:ml-0 p-2 rounded-lg hover:bg-slate-100" aria-label="Menu">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1" data-testid="nav-mobile-menu">
          <form onSubmit={submit} className="flex items-center relative mb-2">
            <Search className="w-4 h-4 absolute left-3 text-slate-400" />
            <input data-testid="nav-mobile-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="pl-9 pr-4 py-2 w-full rounded-full border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400" />
          </form>
          <div className="mb-2"><LanguageSelect compact={false} /></div>
          {!user && (
            <button data-testid="sso-socialpulse-btn-mobile" onClick={() => signIn(window.location.pathname)} className="w-full text-left px-3 py-2 rounded-lg text-sm font-bold text-slate-700 hover:bg-pink-50 hover:text-pink-600">Sign In with SocialPulse</button>
          )}
          {user?.role === "admin" && (
            <NavLink to="/moderation" onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg text-sm font-bold text-purple-700 hover:bg-purple-50">Moderation Dashboard</NavLink>
          )}
          {LINKS.map((l) => (
            <NavLink key={l.name} to={l.path} onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-pink-50 hover:text-pink-600">
              {l.name}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
};

export default Navbar;
